/**
 * Zone controls shared by the Inspector (today's layout) and the Brief panel
 * (the `briefRedesign` layout): the zone-type/category selector and the
 * GRZ/GFZ compliance readout. Kept in one place so both panels render the same
 * control instead of forking.
 */
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useBoolean } from "src/hooks/useBoolean";
import { useWindowEvent } from "src/hooks/useWindowEvent";
import { useTranslation } from "src/translations";
import { useFeatureFlag } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";
import { ZoneType, type Zone } from "src/types/Zone";
import { ArchitecturalMetrics } from "src/types/ArchitecturalMetrics";

export const ZoneTypeSelect = observer(function ZoneTypeSelect(props: {
  zone: Zone;
  readonly: boolean;
  onChange: (type: ZoneType) => void;
}) {
  const { t } = useTranslation();
  const subtypesEnabled = useFeatureFlag(FLAGS.exclusionSubtypes);
  const usageOptions = [
    { label: t("zones.buildable"), id: ZoneType.buildable },
    { label: t("zones.exclusion"), id: ZoneType.exclusion },
    ...(subtypesEnabled
      ? ([
          { label: t("zones.parking"), id: ZoneType.parking },
          { label: t("zones.access"), id: ZoneType.access },
          { label: t("zones.greenZones"), id: ZoneType.green_zones },
        ] as const)
      : ([] as const)),
  ] as const;
  return (
    <SelectInput
      options={usageOptions}
      selected={props.zone.zoneType}
      readonly={props.readonly}
      onChange={(id) => props.onChange(id as ZoneType)}
    />
  );
});

export const ZoneComplianceMetrics = observer(function ZoneComplianceMetrics(props: {
  zone: Zone;
}) {
  const { t } = useTranslation();
  const enabled = useFeatureFlag(FLAGS.bplanMetrics);
  if (!enabled) return null;
  if (props.zone.zoneType !== ZoneType.buildable) return null;
  if (!props.zone.hasBuilding) return null;
  const metrics = new ArchitecturalMetrics(props.zone.buildings);
  return (
    <div className="flex flex-col w-full space-y-1">
      <ComplianceRow label={t("design.floors")} value={`${metrics.FloorMax}`} />
      <ComplianceRow label={t("design.grz")} value={metrics.LandCoverageTotal.toFixed(2)} />
      <ComplianceRow label={t("design.gfz")} value={metrics.GfzRatio.toFixed(2)} />
    </div>
  );
});

const ComplianceRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row justify-between flex-1 w-full items-center text-body-1 text-neutral-6">
    <span>{label}</span>
    <span className="text-neutral-8">{value}</span>
  </div>
);

interface SelectInputOption {
  id: string;
  label: string;
}

const SelectInput = observer(function SelectInput<T extends readonly SelectInputOption[]>(props: {
  options: T;
  selected: string;
  readonly: boolean;
  onChange?: (id: T[number]["id"]) => void;
}) {
  const selected = props.options.find((option) => option.id === props.selected) ?? props.options[0];
  const optionVisible = useBoolean();
  useWindowEvent("click", optionVisible.off);
  return (
    <div className="w-[130px] flex flex-row text-body-1 text-neutral-7 h-[24px] items-center">
      <div
        className="relative border border-neutral-5 flex flex-row rounded-sm flex-1"
        onClick={(evt) => evt.stopPropagation()}
      >
        <div
          className={classNames(
            "flex flex-1 flex-row px-[6px] py-[2px]",
            props.readonly && "cursor-default bg-neutral-2",
            !props.readonly && "cursor-pointer"
          )}
          onClick={!props.readonly ? optionVisible.on : undefined}
        >
          <span className="flex-1">{selected.label}</span>
          <ChevronDownIcon className={classNames("w-4", props.readonly && "opacity-0")} />
        </div>
        {optionVisible.isOn && (
          <div className="absolute bg-white w-full border border-neutral-5">
            {props.options.map((option) => (
              <div
                className="px-[6px] hover:bg-primary-1 cursor-pointer"
                key={option.id}
                onClick={
                  props.readonly
                    ? undefined
                    : () => {
                        props.onChange?.(option.id);
                        optionVisible.off();
                      }
                }
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
